import React, { memo, useCallback, useMemo } from "react";
import moment from "moment";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import UniversityIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/University.svg";
import InstituteIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Institute.svg";
import DegreeIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Degree.svg";
import GradeIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Grade.svg";
import FromDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/From_Date.svg";
import ToDateIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/To_Date.svg";
import DescriptionIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Certificate_Description.svg";
import ActionIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Action.svg";

const EmpAcademicTableComponent = memo(({
  data = [],
  canEdit = false,
  onEdit,
  noDataMessage = "No Academic Data Available",
}) => {
  const columns = useMemo(() => [
    { 
      key: "University", 
      label: "University", 
      icon: UniversityIcon,
      width: '16%'
    },
    { 
      key: "Institute", 
      label: "Institute", 
      icon: InstituteIcon,
      width: '16%'
    },
    { 
      key: "Degree", 
      label: "Degree", 
      icon: DegreeIcon,
      width: '14%'
    },
    { 
      key: "Grade", 
      label: "Grade", 
      icon: GradeIcon,
      width: '10%'
    },
    { 
      key: "FromDate", 
      label: "From Date", 
      icon: FromDateIcon, 
      isDate: true,
      center: true,
      width: '10%'
    },
    { 
      key: "ToDate", 
      label: "To Date", 
      icon: ToDateIcon, 
      isDate: true,
      center: true,
      width: '10%'
    },
    { 
      key: "Description", 
      label: "Description", 
      icon: DescriptionIcon,
      width: '18%'
    },
    { 
      key: "action", 
      label: "Action", 
      icon: ActionIcon, 
      isAction: true,
      center: true,
      width: '6%'
    },
  ], []);

  const formatDate = useCallback((date) => {
    return date ? moment(date).format("DD-MMM-YYYY") : "";
  }, []);

  const renderTooltip = useCallback((text) => <Tooltip>{text}</Tooltip>, []);

  const renderCellContent = useCallback((text, isDate = false, center = false) => {
    const displayText = isDate ? formatDate(text) : text || "";
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={renderTooltip(displayText)}
      >
        <div 
          className={`text-truncate ${center ? 'text-center' : ''}`} 
          style={{ 
            width: '100%',
            display: 'inline-block'
          }}
        >
          {displayText}
        </div>
      </OverlayTrigger>
    );
  }, [formatDate, renderTooltip]);

  const handleEdit = useCallback((item) => {
    if (canEdit && onEdit) {
      onEdit(item);
    }
  }, [canEdit, onEdit]);

  return (
    <div className="academic-table-container">
      <style>
        {`
          .academic-table-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .fixed-academic-table {
            table-layout: fixed;
            width: 100%;
            min-width: 900px; /* Minimum width to ensure columns don't collapse */
          }
          
          .fixed-width-column {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (min-width: 992px) {
            .fixed-academic-table {
              min-width: 100%;
            }
          }
        `}
      </style>
      
      <Table striped bordered hover className="fixed-academic-table">
        <thead className="text-center">
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key} 
                className="text-center"
                style={{ width: column.width }}
              >
                <div className="d-flex flex-column align-items-center fixed-width-column">
                  <div>
                    <img
                      src={column.icon}
                      alt={column.label}
                      className="tech-icon"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </div>
                  <div style={{ fontSize: "12px" }}>{column.label}</div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => {
                  if (column.isAction) {
                    return (
                      <td 
                        key={column.key} 
                        className="text-center fixed-width-column" 
                        style={{ width: column.width }}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{canEdit ? "Edit" : "Edit disabled"}</Tooltip>}
                        >
                          <span
                            onClick={() => handleEdit(item)}
                            style={{
                              cursor: canEdit ? "pointer" : "not-allowed",
                              opacity: canEdit ? 1 : 0.5,
                            }}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </span>
                        </OverlayTrigger>
                      </td>
                    );
                  }
                  
                  return (
                    <td 
                      key={column.key} 
                      className={`fixed-width-column ${column.center ? "text-center" : ""}`}
                      style={{ width: column.width }}
                    >
                      {renderCellContent(
                        item[column.key], 
                        column.isDate, 
                        column.center
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center">
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
});

export default EmpAcademicTableComponent;